/**
 * GradingMatrix — Reusable Stage × Grade input matrix
 *
 * Renders a compact HTML table with editable number inputs, auto-calculated
 * row/column totals, grand total, and an optional Clear button.
 * Supports arrow-key / Tab / Enter keyboard navigation between cells.
 *
 * Accepts the same GradingGridRow data shape as the legacy AG Grid component
 * so consumers can swap in without changing their data layer.
 */
import { memo, useCallback, useMemo, useRef } from 'react';

import { Eraser } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';

import { GradingGridRow } from './types';

const STAGES = [9, 8, 7, 6, 5, 4] as const;
const UNKNOWN_STAGE = 0;
const GRADES = [1, 2, 3] as const;
type Stage = (typeof STAGES)[number] | typeof UNKNOWN_STAGE;
type StageField = `s${Stage}`;

export interface GradingMatrixProps {
  /** Current row data (one row per grade: 1, 2, 3). */
  data: GradingGridRow[];
  /** Called whenever a cell value changes. Receives the full updated rows array. */
  onChange: (rows: GradingGridRow[]) => void;
  /** Called when the Clear button is clicked. If omitted the button is hidden. */
  onClear?: () => void;
  /** Optional title above the matrix. Defaults to "Grading Matrix". */
  title?: string;
  /** Whether the matrix is read-only. */
  disabled?: boolean;
  /** Stretches the matrix to its container width instead of the fixed 30rem. */
  fullWidth?: boolean;
  /** Adds a final Unknown column for counts with a known grade and no stage (row field `s0`). */
  showUnknownStage?: boolean;
  /** Header for the Unknown column. Defaults to "Unknown". */
  unknownStageLabel?: string;
}

export const GradingMatrix = memo(function GradingMatrix({
  data,
  onChange,
  onClear,
  title = 'Grading Matrix',
  disabled = false,
  fullWidth = false,
  showUnknownStage = false,
  unknownStageLabel = 'Unknown',
}: GradingMatrixProps) {
  const stages: readonly Stage[] = useMemo(
    () => (showUnknownStage ? [...STAGES, UNKNOWN_STAGE] : STAGES),
    [showUnknownStage],
  );
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const widthClass = fullWidth ? 'w-full' : 'w-[30rem]';

  // Build a lookup: grade → { s4, s5, ... }
  const byGrade = useMemo(() => {
    const map = new Map<number, GradingGridRow>();
    for (const row of data) map.set(row.grade, row);
    return map;
  }, [data]);

  const handleChange = useCallback(
    (grade: number, stage: number, raw: string) => {
      const val = raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0);
      const field = `s${stage}` as StageField;
      const updated = data.map((row) =>
        row.grade === grade ? { ...row, [field]: val } : row,
      );
      // Recompute totals
      for (const row of updated) {
        row.total = stages.reduce<number>(
          (sum, s) => sum + (Number(row[`s${s}` as StageField]) || 0),
          0,
        );
      }
      onChange(updated);
    },
    [data, onChange, stages],
  );

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      grade: number,
      stage: number,
    ) => {
      const gi = GRADES.indexOf(grade as (typeof GRADES)[number]);
      const si = stages.indexOf(stage as Stage);
      let nextGi = gi;
      let nextSi = si;

      if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
        nextSi = si + 1;
        if (nextSi >= stages.length) {
          nextSi = 0;
          nextGi = (gi + 1) % GRADES.length;
        }
        e.preventDefault();
      } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
        nextSi = si - 1;
        if (nextSi < 0) {
          nextSi = stages.length - 1;
          nextGi = (gi - 1 + GRADES.length) % GRADES.length;
        }
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        nextGi = (gi + 1) % GRADES.length;
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        nextGi = (gi - 1 + GRADES.length) % GRADES.length;
        e.preventDefault();
      } else if (e.key === 'Enter') {
        nextGi = (gi + 1) % GRADES.length;
        e.preventDefault();
      } else {
        return;
      }

      const key = `${GRADES[nextGi]}-${stages[nextSi]}`;
      inputRefs.current[key]?.focus();
      inputRefs.current[key]?.select();
    },
    [stages],
  );

  const colTotals = useMemo(
    () =>
      Object.fromEntries(
        stages.map((s) => [
          s,
          GRADES.reduce(
            (sum, g) =>
              sum + (Number(byGrade.get(g)?.[`s${s}` as StageField]) || 0),
            0,
          ),
        ]),
      ) as Record<number, number>,
    [byGrade, stages],
  );

  const grandTotal = useMemo(
    () => stages.reduce<number>((sum, s) => sum + (colTotals[s] ?? 0), 0),
    [colTotals, stages],
  );

  const hasData = useMemo(
    () =>
      GRADES.some((g) =>
        stages.some(
          (s) => (Number(byGrade.get(g)?.[`s${s}` as StageField]) || 0) > 0,
        ),
      ),
    [byGrade, stages],
  );

  return (
    <div
      className={`preflight flex flex-col gap-2 ${fullWidth ? 'w-full' : 'w-fit'}`}
    >
      <Field className="w-full">
        {title && <FieldLabel>{title}</FieldLabel>}

        <div
          className={`border-border/60 bg-background overflow-hidden rounded-xl border shadow-xs ${fullWidth ? 'block' : 'inline-block'}`}
        >
          <table
            className={`${widthClass} table-fixed border-collapse text-center text-sm`}
          >
            <thead>
              <tr>
                <th className="text-muted-foreground border-border/60 w-20 border-b px-3 pt-2.5 pb-2 text-left text-[11px] font-medium tracking-wide uppercase">
                  Grade
                </th>
                {stages.map((s) => (
                  <th
                    key={s}
                    className={`text-muted-foreground border-border/60 border-b px-1 pt-2.5 pb-2 text-[11px] font-medium tracking-wide uppercase ${s === UNKNOWN_STAGE ? 'w-20' : 'w-14'}`}
                  >
                    {s === UNKNOWN_STAGE ? unknownStageLabel : `S${s}`}
                  </th>
                ))}
                <th className="text-muted-foreground border-border/60 border-l-border/60 w-16 border-b border-l px-2 pt-2.5 pb-2 text-[11px] font-medium tracking-wide uppercase">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {GRADES.map((grade) => {
                const row = byGrade.get(grade);
                const rowTotal = row?.total ?? 0;
                return (
                  <tr
                    key={grade}
                    className="border-border/40 hover:bg-muted/20 border-b transition-colors last:border-b-0"
                  >
                    <td className="text-muted-foreground px-3 py-1 text-left text-xs font-medium">
                      Grade {grade}
                    </td>
                    {stages.map((stage) => {
                      const key = `${grade}-${stage}`;
                      const val = row?.[`s${stage}` as StageField];
                      return (
                        <td key={stage} className="p-0.5">
                          <input
                            ref={(el) => {
                              inputRefs.current[key] = el;
                            }}
                            type="number"
                            min={0}
                            value={val ?? ''}
                            disabled={disabled}
                            onChange={(e) =>
                              handleChange(grade, stage, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(e, grade, stage)}
                            onFocus={(e) => e.target.select()}
                            className="text-foreground hover:bg-muted/40 focus:bg-primary/5 focus:ring-primary/40 h-8 w-full min-w-0 [appearance:textfield] rounded-md bg-transparent text-center text-sm transition-colors outline-none focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </td>
                      );
                    })}
                    <td className="border-border/60 text-foreground border-l px-2 py-1 text-sm font-semibold tabular-nums">
                      {rowTotal || (
                        <span className="text-muted-foreground/40">·</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-border/60 border-t">
                <td className="text-muted-foreground px-3 py-2 text-left text-[11px] font-medium tracking-wide uppercase">
                  Total
                </td>
                {stages.map((s) => (
                  <td
                    key={s}
                    className="text-foreground px-1 py-2 text-sm font-semibold tabular-nums"
                  >
                    {colTotals[s] || (
                      <span className="text-muted-foreground/40">·</span>
                    )}
                  </td>
                ))}
                <td className="border-border/60 text-primary border-l px-2 py-2 text-sm font-bold tabular-nums">
                  {grandTotal || (
                    <span className="text-muted-foreground/40">·</span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>

          <div
            className={`border-border/60 flex ${widthClass} items-center justify-between gap-3 border-t px-3 py-1.5`}
          >
            <span className="text-muted-foreground text-xs">
              {grandTotal > 0 ? (
                <>
                  <span className="text-foreground font-medium">
                    {grandTotal}
                  </span>{' '}
                  total embryo{grandTotal !== 1 ? 's' : ''}
                </>
              ) : (
                'Stage × grade counts'
              )}
            </span>
            {onClear && (
              <Button
                variant="ghost"
                size="xs"
                onClick={onClear}
                disabled={disabled || !hasData}
                className="text-muted-foreground shrink-0"
              >
                <Eraser className="size-3.5" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </Field>
    </div>
  );
});
